import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Activity,
  Apple,
  ArrowLeft,
  BarChart2,
  Beef,
  Bell,
  BookOpen,
  Bot,
  Calendar,
  CalendarRange,
  CheckCircle,
  ChefHat,
  ClipboardList,
  Copy,
  Download,
  Droplets,
  FileText,
  Flame,
  FolderOpen,
  History,
  LayoutDashboard,
  Link2,
  MessageSquare,
  RotateCcw,
  Ruler,
  Save,
  Trash2,
  TrendingUp,
  User,
  Users,
  Utensils,
  Video,
  XCircle,
  Zap,
} from 'lucide-react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import foodLogoMark from '../../modules/food_version_final/frontend/src/iconosFoodPlan/food-logo-mark.png';
import {
  FOOD_NAV_FINAL,
  FOOD_NAV_ENTRENADOR,
  DEMO_LOCAL_MEALS,
} from './demoFixtures';
import { FINAL_GUIDE, TRAINER_GUIDE } from './demoGuideSteps';
import DemoGuide from './DemoGuide';
import {
  DemoPlanView,
  DemoNutritionView,
  DemoRecipesView,
  DemoChatView,
  DemoMeasuresView,
  DemoReportView,
} from './DemoUserScreens';
import { todayYmd, uid, shiftYmd } from './demoStore';
import '../food-plan/food-tailwind.css';

const USER_ICONS = {
  dashboard: LayoutDashboard,
  nutrition: Utensils,
  chat: Bot,
  plan: ClipboardList,
  measures: Ruler,
  report: FileText,
  recipes: ChefHat,
  mytrainer: Users,
  profile: User,
};

const TRAINER_ICONS = {
  panel: Users,
  localmeals: Utensils,
  vigencias: CalendarRange,
  notifications: Bell,
  reminder: Ruler,
  profile: User,
};

const DAYS_ES = ['D', 'L', 'M', 'X', 'J', 'V', 'S'];

function MacroCard({ label, current, goal, color, icon: Icon, unit = 'g' }) {
  const pct = goal > 0 ? Math.min(Math.round((current / goal) * 100), 100) : 0;
  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className={`p-1.5 rounded-lg ${color}`}><Icon size={16} className="text-white" /></div>
          <span className="font-medium text-gray-700 text-sm">{label}</span>
        </div>
        <span className="text-xs text-gray-400">{pct}%</span>
      </div>
      <div className="flex items-end justify-between">
        <span className="text-xl font-bold text-gray-800">{Math.round(current)}</span>
        <span className="text-xs text-gray-400">/ {goal}{unit}</span>
      </div>
      <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${pct >= 100 ? 'bg-green-500' : pct >= 70 ? 'bg-yellow-400' : 'bg-red-400'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function buildCalendarMarked() {
  const today = todayYmd();
  const map = {};
  for (let i = 0; i < 18; i += 1) {
    if (i % 3 !== 0) map[shiftYmd(today, -i)] = true;
  }
  return map;
}

function tlEmoji(tl) {
  if (tl === 'GREEN') return '🟢';
  if (tl === 'YELLOW') return '🟡';
  if (tl === 'RED') return '🔴';
  return '⚪';
}

function tlAvatar(tl) {
  if (tl === 'GREEN') return 'bg-green-500';
  if (tl === 'YELLOW') return 'bg-yellow-400';
  if (tl === 'RED') return 'bg-red-500';
  return 'bg-gray-300';
}

/** Series demo para el modal Reportes (paridad con TrainerDashboard). */
function buildStudentFollowUp(student) {
  const today = todayYmd();
  const hydrationDays = Array.from({ length: 14 }, (_, i) => {
    const date = shiftYmd(today, i - 13);
    const glasses = Math.max(2, Math.min(10, 4 + (i % 5) + (student.adherence > 70 ? 2 : 0)));
    const steps = Math.round(3500 + i * 220 + (student.adherence || 40) * 25 + (i % 3) * 400);
    return {
      date,
      label: new Date(`${date}T12:00:00`).toLocaleDateString('es-CO', { day: 'numeric', month: 'short' }),
      glasses,
      steps,
      waterGoal: 8,
      stepsGoal: 8000,
    };
  });

  const wellbeing = Array.from({ length: 10 }, (_, i) => {
    const date = shiftYmd(today, (i - 9) * 2);
    return {
      date,
      label: new Date(`${date}T12:00:00`).toLocaleDateString('es-CO', { day: 'numeric', month: 'short' }),
      sueño: 5 + (i % 4),
      energía: 4 + ((i + 1) % 5),
      estrés: 6 - (i % 4),
      peso: Math.round(((Number(student.weight) || 70) - i * 0.15 + (i % 2) * 0.2) * 10) / 10,
    };
  });

  const foodDays = Array.from({ length: 5 }, (_, i) => {
    const date = shiftYmd(today, -i * 2);
    const totalCalories = 1400 + i * 80 + (student.adherence || 40) * 4;
    return {
      date,
      count: 4 + (i % 3),
      totalCalories,
      totalProtein: Math.round(totalCalories * 0.12 / 4),
      totalCarbs: Math.round(totalCalories * 0.45 / 4),
      totalFat: Math.round(totalCalories * 0.3 / 9),
    };
  });

  const dailyReports = Array.from({ length: 5 }, (_, i) => {
    const reportDate = shiftYmd(today, -i * 2);
    return {
      id: `dr_${student.id}_${i}`,
      reportDate,
      weightKg: Math.round(((Number(student.weight) || 70) - i * 0.12) * 10) / 10,
      performance: ['BUENO', 'NORMAL', 'MUY_BUENO', 'NORMAL', 'BUENO'][i],
      motivation: ['ALTA', 'NORMAL', 'ALTA', 'BAJA', 'NORMAL'][i],
      hunger: ['NORMAL', 'ALTA', 'NORMAL', 'BAJA', 'NORMAL'][i],
      fatigue: ['BAJA', 'NORMAL', 'BAJA', 'ALTA', 'NORMAL'][i],
      stress: ['BAJO', 'NORMAL', 'BAJO', 'ALTO', 'NORMAL'][i],
      sleepHours: 6.5 + (i % 3) * 0.5,
      sleepQuality: ['BUENO', 'NORMAL', 'MUY_BUENO', 'NORMAL', 'BUENO'][i],
      mood: ['BUENO', 'NORMAL', 'BUENO', 'MALO', 'BUENO'][i],
    };
  });

  return {
    plan: {
      dailyCalories: student.adherence > 70 ? 2200 : 1800,
      dailyProteinG: student.adherence > 70 ? 150 : 120,
      dailyCarbsG: 200,
      dailyFatG: 60,
      dailyWaterGlasses: 8,
      dailySteps: 8000,
      goalType: 'LOSE',
      activityLevel: 'Moderada',
      trainerOverride: true,
    },
    hydrationDays,
    wellbeing,
    foodDays,
    dailyReports,
  };
}

function ReportsFollowUpModal({ student, onClose }) {
  const [tab, setTab] = useState('reports');
  const data = useMemo(() => buildStudentFollowUp(student), [student]);
  const [planForm, setPlanForm] = useState(() => ({
    dailyCalories: String(data.plan.dailyCalories),
    dailyProteinG: String(data.plan.dailyProteinG),
    dailyCarbsG: String(data.plan.dailyCarbsG),
    dailyFatG: String(data.plan.dailyFatG),
    dailyWaterGlasses: String(data.plan.dailyWaterGlasses),
    dailySteps: String(data.plan.dailySteps),
  }));
  const [foodLogDate, setFoodLogDate] = useState(null);
  const [toast, setToast] = useState('');

  const showToast = (msg) => {
    setToast(msg);
    window.setTimeout(() => setToast(''), 1800);
  };

  const hydrationChartData = data.hydrationDays.slice(-14);

  const modal = (
    <div className="food-plan-root food-portal-modal fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
      <div className="food-demo-modal-panel bg-white rounded-2xl p-6 max-w-3xl w-full shadow-xl max-h-[90vh] flex flex-col relative border border-gray-200">
        {toast ? (
          <div className="absolute right-4 top-4 z-10 rounded-lg border border-green-200 bg-green-50 px-3 py-1.5 text-xs font-semibold text-green-800">
            {toast}
          </div>
        ) : null}
        <div className="flex items-center justify-between mb-4 flex-shrink-0">
          <div>
            <h3 className="font-bold text-gray-800 text-lg">{student.firstName} {student.lastName}</h3>
            <p className="text-xs text-gray-400 mt-0.5">Panel de seguimiento</p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XCircle size={22} />
          </button>
        </div>

        <div className="relative z-10 flex gap-1 mb-4 bg-gray-100 p-1 rounded-xl flex-shrink-0">
          {[
            { id: 'plan', label: 'Plan Nutricional' },
            { id: 'reports', label: 'Reportes Diarios' },
            { id: 'foods', label: 'Comidas' },
            { id: 'hydration', label: 'Agua y Pasos' },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setTab(t.id);
                setFoodLogDate(null);
              }}
              className={`flex-1 py-2 rounded-lg text-xs font-medium transition-all ${
                tab === t.id ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="overflow-y-auto flex-1 min-h-0" key={tab}>
          {tab === 'plan' ? (
            <div className="border border-purple-100 rounded-xl p-4 bg-purple-50">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-bold text-purple-700">Plan Nutricional (Personalizado)</p>
                <button type="button" onClick={() => showToast('Plan restaurado (demo)')} className="text-xs text-gray-400 hover:text-red-500 flex items-center gap-1">
                  <RotateCcw size={11} /> Restaurar
                </button>
              </div>
              <div className="grid grid-cols-4 gap-2 mb-4 bg-white rounded-lg p-3">
                <div className="text-center"><p className="text-xs text-gray-400">Calorías</p><p className="text-sm font-bold text-gray-700">{data.plan.dailyCalories} kcal</p></div>
                <div className="text-center"><p className="text-xs text-gray-400">Proteína</p><p className="text-sm font-bold text-gray-700">{data.plan.dailyProteinG} g</p></div>
                <div className="text-center"><p className="text-xs text-gray-400">Carbos</p><p className="text-sm font-bold text-gray-700">{data.plan.dailyCarbsG} g</p></div>
                <div className="text-center"><p className="text-xs text-gray-400">Grasas</p><p className="text-sm font-bold text-gray-700">{data.plan.dailyFatG} g</p></div>
                <div className="text-center"><p className="text-xs text-gray-400">Agua</p><p className="text-sm font-bold text-gray-700">{data.plan.dailyWaterGlasses} vasos</p></div>
                <div className="text-center"><p className="text-xs text-gray-400">Pasos</p><p className="text-sm font-bold text-gray-700">{data.plan.dailySteps}</p></div>
                <div className="text-center"><p className="text-xs text-gray-400">Objetivo</p><p className="text-sm font-bold text-gray-700">Perder</p></div>
                <div className="text-center"><p className="text-xs text-gray-400">Actividad</p><p className="text-sm font-bold text-gray-700">{data.plan.activityLevel}</p></div>
              </div>
              <p className="text-xs font-semibold text-purple-600 mb-2">Ajustar valores:</p>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { key: 'dailyCalories', label: 'Calorías', unit: 'kcal' },
                  { key: 'dailyProteinG', label: 'Proteínas', unit: 'g' },
                  { key: 'dailyCarbsG', label: 'Carbos', unit: 'g' },
                  { key: 'dailyFatG', label: 'Grasas', unit: 'g' },
                  { key: 'dailyWaterGlasses', label: 'Agua', unit: 'vasos' },
                  { key: 'dailySteps', label: 'Pasos', unit: 'pasos' },
                ].map(({ key, label, unit }) => (
                  <div key={key}>
                    <label className="text-xs text-gray-500 block mb-0.5">{label}</label>
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        className="input text-xs py-1 px-2 flex-1"
                        value={planForm[key]}
                        onChange={(e) => setPlanForm((f) => ({ ...f, [key]: e.target.value }))}
                      />
                      <span className="text-xs text-gray-400">{unit}</span>
                    </div>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={() => showToast('Plan guardado (demo · sin BD)')}
                className="mt-3 w-full text-xs py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium flex items-center justify-center gap-1"
              >
                <Save size={12} /> Guardar plan
              </button>
            </div>
          ) : null}

          {tab === 'reports' ? (
            <div className="space-y-4">
              <div className="h-48 bg-gray-50 rounded-xl p-3 border border-gray-100">
                <p className="text-xs font-semibold text-gray-600 mb-2">Bienestar · tendencia</p>
                <ResponsiveContainer width="100%" height="85%">
                  <LineChart data={data.wellbeing} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 9 }} />
                    <YAxis domain={[0, 10]} tick={{ fontSize: 9 }} width={24} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                    <Line type="monotone" dataKey="sueño" name="Sueño (h)" stroke="#6366f1" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="energía" name="Energía" stroke="#22c55e" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="estrés" name="Estrés" stroke="#f59e0b" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
                <p className="text-[10px] text-gray-400 mt-1 text-center">Sueño en horas · Energía/Estrés en escala 0–10</p>
              </div>
              <div className="h-44 bg-gray-50 rounded-xl p-3 border border-gray-100">
                <p className="text-xs font-semibold text-gray-600 mb-2">Peso en reportes</p>
                <ResponsiveContainer width="100%" height="85%">
                  <LineChart data={data.wellbeing} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 9 }} />
                    <YAxis domain={['dataMin - 1', 'dataMax + 1']} tick={{ fontSize: 9 }} width={32} />
                    <Tooltip />
                    <Line type="monotone" dataKey="peso" name="kg" stroke="#ec4899" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              {data.dailyReports.map((r) => (
                <div key={r.id} className="border border-gray-100 rounded-xl p-4">
                  <div className="flex justify-between items-center mb-3">
                    <p className="font-medium text-gray-800 text-sm capitalize">
                      {new Date(`${r.reportDate}T12:00:00`).toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long' })}
                    </p>
                    {r.weightKg ? <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full font-medium">{r.weightKg} kg</span> : null}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-gray-50 rounded-lg px-2 py-1"><span className="text-gray-400">Rendimiento: </span><span className="font-medium text-gray-700">{r.performance}</span></div>
                    <div className="bg-gray-50 rounded-lg px-2 py-1"><span className="text-gray-400">Motivación: </span><span className="font-medium text-gray-700">{r.motivation}</span></div>
                    <div className="bg-gray-50 rounded-lg px-2 py-1"><span className="text-gray-400">Hambre: </span><span className="font-medium text-gray-700">{r.hunger}</span></div>
                    <div className="bg-gray-50 rounded-lg px-2 py-1"><span className="text-gray-400">Fatiga: </span><span className="font-medium text-gray-700">{r.fatigue}</span></div>
                    <div className="bg-gray-50 rounded-lg px-2 py-1"><span className="text-gray-400">Estrés: </span><span className="font-medium text-gray-700">{r.stress}</span></div>
                    <div className="bg-gray-50 rounded-lg px-2 py-1"><span className="text-gray-400">Sueño: </span><span className="font-medium text-gray-700">{r.sleepHours}h</span></div>
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          {tab === 'foods' ? (
            <div>
              {!foodLogDate ? (
                <div className="space-y-2">
                  {data.foodDays.map((day) => (
                    <button
                      key={day.date}
                      type="button"
                      onClick={() => setFoodLogDate(day.date)}
                      className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-primary-50 hover:border-primary-200 border border-gray-100 rounded-xl transition-all text-left"
                    >
                      <div>
                        <p className="text-sm font-medium text-gray-800 capitalize">
                          {new Date(`${day.date}T12:00:00`).toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long' })}
                        </p>
                        <p className="text-xs text-gray-400">{day.count} alimentos registrados</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-primary-600">{Math.round(day.totalCalories)} kcal</p>
                        <p className="text-xs text-gray-400">P:{day.totalProtein}g C:{day.totalCarbs}g G:{day.totalFat}g</p>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="space-y-3">
                  <button type="button" className="text-xs text-gray-400 hover:text-gray-600 underline" onClick={() => setFoodLogDate(null)}>
                    ← Ver todos
                  </button>
                  <p className="text-sm font-semibold text-gray-700 capitalize">
                    {new Date(`${foodLogDate}T12:00:00`).toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long' })}
                  </p>
                  <div className="grid grid-cols-4 gap-2 mb-3 bg-gray-50 rounded-xl p-3">
                    <div className="text-center"><p className="text-xs text-gray-400">Calorías</p><p className="text-sm font-bold text-gray-700">1680 kcal</p></div>
                    <div className="text-center"><p className="text-xs text-gray-400">Proteína</p><p className="text-sm font-bold text-gray-700">98 g</p></div>
                    <div className="text-center"><p className="text-xs text-gray-400">Carbos</p><p className="text-sm font-bold text-gray-700">160 g</p></div>
                    <div className="text-center"><p className="text-xs text-gray-400">Grasas</p><p className="text-sm font-bold text-gray-700">52 g</p></div>
                  </div>
                  {[
                    { meal: 'Desayuno', items: [['Avena con frutas', '320 kcal'], ['Café', '5 kcal']] },
                    { meal: 'Almuerzo', items: [['Pollo + arroz', '480 kcal'], ['Ensalada', '90 kcal']] },
                    { meal: 'Cena', items: [['Salmón + verduras', '420 kcal']] },
                    { meal: 'Snack', items: [['Yogur griego', '120 kcal']] },
                  ].map((block) => (
                    <div key={block.meal} className="bg-gray-50 rounded-xl p-3">
                      <p className="text-sm font-semibold text-gray-700 mb-2">{block.meal}</p>
                      {block.items.map(([name, kcal]) => (
                        <div key={name} className="flex justify-between text-xs text-gray-600 py-1 border-b border-gray-100 last:border-0">
                          <span>{name}</span>
                          <span className="text-gray-400">{kcal}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : null}

          {tab === 'hydration' ? (
            <div>
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <div className="flex items-center gap-1 text-sm text-gray-500"><Calendar size={14} /> Rango demo</div>
                <button type="button" onClick={() => showToast('Filtro aplicado (demo)')} className="btn-primary text-xs py-1.5 px-3">Filtrar</button>
                <button type="button" onClick={() => showToast('Excel simulado')} className="flex items-center gap-1.5 text-xs py-1.5 px-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg">
                  <Download size={14} /> Exportar Excel
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2 mb-3">
                <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 text-center">
                  <p className="text-xs text-blue-600 flex items-center justify-center gap-1"><Droplets size={12} /> Meta agua</p>
                  <p className="text-lg font-bold text-blue-700">8 vasos</p>
                </div>
                <div className="bg-green-50 border border-green-100 rounded-xl p-3 text-center">
                  <p className="text-xs text-green-600">Meta pasos</p>
                  <p className="text-lg font-bold text-green-700">8.000</p>
                </div>
              </div>
              <div className="h-52 min-h-[13rem] mb-4 bg-gray-50 rounded-xl p-3 border border-gray-100">
                <p className="text-xs font-semibold text-gray-600 mb-2">Tendencia (últimos {hydrationChartData.length} días)</p>
                <ResponsiveContainer width="100%" height="85%">
                  <AreaChart data={hydrationChartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 9 }} minTickGap={16} />
                    <YAxis yAxisId="water" tick={{ fontSize: 9 }} width={28} />
                    <YAxis yAxisId="steps" orientation="right" tick={{ fontSize: 9 }} width={36} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                    <Area yAxisId="water" type="monotone" dataKey="glasses" name="Agua (vasos)" stroke="#3b82f6" fill="#93c5fd" fillOpacity={0.35} />
                    <Area yAxisId="steps" type="monotone" dataKey="steps" name="Pasos" stroke="#22c55e" fill="#86efac" fillOpacity={0.25} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-2">
                {[...hydrationChartData].reverse().slice(0, 6).map((day) => {
                  const waterPct = Math.min(100, Math.round((day.glasses / day.waterGoal) * 100));
                  const stepsPct = Math.min(100, Math.round((day.steps / day.stepsGoal) * 100));
                  return (
                    <div key={day.date} className="border border-gray-100 rounded-xl p-3 bg-white">
                      <p className="text-sm font-medium text-gray-800 mb-2 capitalize">
                        {new Date(`${day.date}T12:00:00`).toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long' })}
                      </p>
                      <div className="space-y-2">
                        <div>
                          <div className="flex justify-between text-xs text-gray-500 mb-1">
                            <span className="flex items-center gap-1"><Droplets size={11} className="text-blue-500" /> Agua</span>
                            <span>{day.glasses} / {day.waterGoal} vasos ({waterPct}%)</span>
                          </div>
                          <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full bg-blue-500 rounded-full" style={{ width: `${waterPct}%` }} />
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-xs text-gray-500 mb-1">
                            <span>Pasos</span>
                            <span>{day.steps.toLocaleString()} / {day.stepsGoal.toLocaleString()} ({stepsPct}%)</span>
                          </div>
                          <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full bg-green-500 rounded-full" style={{ width: `${stepsPct}%` }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}

function UsualFilesDemoModal({ student, onClose }) {
  const [tab, setTab] = useState('meals');
  const meals = [
    { id: 'um1', name: 'Hamburguesa El Corral', kcal: 680, protein: 34, carbs: 52, fat: 36 },
    { id: 'um2', name: 'Ensalada César', kcal: 420, protein: 22, carbs: 18, fat: 28 },
    { id: 'um3', name: 'Crepes de pollo', kcal: 540, protein: 28, carbs: 46, fat: 24 },
  ];
  const files = [
    { id: 'f1', name: 'Plan_semana.pdf', size: '240 KB', date: shiftYmd(todayYmd(), -3) },
    { id: 'f2', name: 'Foto_progreso.jpg', size: '1.2 MB', date: shiftYmd(todayYmd(), -8) },
    { id: 'f3', name: 'Notas_consulta.docx', size: '88 KB', date: shiftYmd(todayYmd(), -14) },
  ];

  const modal = (
    <div className="food-plan-root food-portal-modal fixed inset-0 bg-black/50 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="food-demo-modal-panel bg-white w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl max-h-[92vh] overflow-hidden flex flex-col shadow-xl border border-gray-200">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-white">
          <div>
            <h3 className="font-bold text-gray-800">{student.firstName} {student.lastName}</h3>
            <p className="text-xs text-gray-500">Comidas habituales y archivos</p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-700">
            <XCircle size={20} />
          </button>
        </div>
        <div className="flex gap-2 px-4 pt-3">
          <button
            type="button"
            className={`text-sm px-3 py-1.5 rounded-lg font-medium ${tab === 'meals' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600'}`}
            onClick={() => setTab('meals')}
          >
            <span className="inline-flex items-center gap-1"><BookOpen size={14} /> Habituales</span>
          </button>
          <button
            type="button"
            className={`text-sm px-3 py-1.5 rounded-lg font-medium ${tab === 'files' ? 'bg-teal-100 text-teal-700' : 'bg-gray-100 text-gray-600'}`}
            onClick={() => setTab('files')}
          >
            <span className="inline-flex items-center gap-1"><FolderOpen size={14} /> Archivos</span>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {tab === 'meals' ? (
            <div className="space-y-3">
              {meals.map((m) => (
                <div key={m.id} className="border border-gray-100 rounded-xl p-3 bg-gray-50">
                  <p className="font-semibold text-gray-800 text-sm">{m.name}</p>
                  <p className="text-xs text-gray-500 mt-1">{m.kcal} kcal · P {m.protein}g · C {m.carbs}g · G {m.fat}g</p>
                </div>
              ))}
              <p className="text-xs text-gray-400 text-center pt-2">Demo · sin escritura a BD</p>
            </div>
          ) : (
            <div className="space-y-2">
              {files.map((f) => (
                <div key={f.id} className="flex items-center justify-between border border-gray-100 rounded-xl px-3 py-2.5 bg-white">
                  <div>
                    <p className="text-sm font-medium text-gray-800">{f.name}</p>
                    <p className="text-xs text-gray-400">{f.size} · {f.date}</p>
                  </div>
                  <span className="text-xs text-teal-600 font-medium">Demo</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}

function PersonaSwitch({ persona, setPersona, onBackToEcosystem }) {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Ver como</span>
      <button
        type="button"
        className={persona === 'final' ? 'btn-primary text-xs py-1.5 px-3' : 'btn-secondary text-xs py-1.5 px-3'}
        onClick={() => setPersona('final')}
      >
        Usuario final
      </button>
      <button
        type="button"
        className={persona === 'entrenador' ? 'btn-primary text-xs py-1.5 px-3' : 'btn-secondary text-xs py-1.5 px-3'}
        onClick={() => setPersona('entrenador')}
      >
        Usuario entrenador
      </button>
      <button type="button" className="md:hidden btn-secondary text-xs py-1.5 px-3" onClick={onBackToEcosystem}>
        Ecosistema
      </button>
    </div>
  );
}

function TrainerPanel({ state, setState }) {
  const trainer = state.foodTrainer;
  const students = trainer.students || [];
  const [search, setSearch] = useState('');
  const [trafficFilter, setTrafficFilter] = useState('');
  const [toast, setToast] = useState('');
  const [action, setAction] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [copied, setCopied] = useState(false);
  const tab = state.ui.foodTrainerTab || 'students';

  const setTab = (foodTrainerTab) => {
    setState((prev) => ({ ...prev, ui: { ...prev.ui, foodTrainerTab } }));
  };

  const showToast = (msg) => {
    setToast(msg);
    window.setTimeout(() => setToast(''), 2200);
  };

  const stats = useMemo(() => ({
    total: students.length,
    green: students.filter((s) => s.trafficLight === 'GREEN').length,
    yellow: students.filter((s) => s.trafficLight === 'YELLOW').length,
    red: students.filter((s) => s.trafficLight === 'RED').length,
  }), [students]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return students.filter((s) => {
      if (trafficFilter && s.trafficLight !== trafficFilter) return false;
      if (!q) return true;
      const name = `${s.firstName} ${s.lastName}`.toLowerCase();
      return name.includes(q) || String(s.email || '').toLowerCase().includes(q);
    });
  }, [students, search, trafficFilter]);

  const patchStudent = (id, updater) => {
    setState((prev) => ({
      ...prev,
      foodTrainer: {
        ...prev.foodTrainer,
        students: prev.foodTrainer.students.map((s) => (s.id === id ? updater(s) : s)),
      },
    }));
  };

  const sendNote = () => {
    if (!action?.student || !noteText.trim()) return;
    const text = noteText.trim();
    patchStudent(action.student.id, (s) => ({
      ...s,
      notes: [{ id: uid('fn'), at: todayYmd(), text }, ...(s.notes || [])],
    }));
    setNoteText('');
    setAction(null);
    showToast('Nota enviada (demo · sin BD)');
  };

  const unlink = (student) => {
    setState((prev) => ({
      ...prev,
      foodTrainer: {
        ...prev.foodTrainer,
        students: prev.foodTrainer.students.filter((s) => s.id !== student.id),
      },
    }));
    setAction(null);
    showToast(`${student.firstName} desvinculado (solo demo)`);
  };

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto relative">
      {toast ? (
        <div className="absolute right-4 top-4 z-10 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm font-semibold text-green-800 shadow-sm">
          {toast}
        </div>
      ) : null}

      <h1 className="text-2xl font-bold text-gray-800 mb-6">Panel del Entrenador</h1>

      <div className="flex gap-1 mb-6 border-b border-gray-200 overflow-x-auto pb-px">
        {[
          { id: 'students', label: 'Mis Asesorados', icon: Users },
          { id: 'code', label: 'Mi Código', icon: Link2 },
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              tab === id ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Icon size={16} />{label}
          </button>
        ))}
      </div>

      {(trainer.inbox || []).some((n) => n.unread) ? (
        <button
          type="button"
          onClick={() => setState((prev) => ({ ...prev, ui: { ...prev.ui, foodView: 'notifications' } }))}
          className="mb-4 w-full flex items-center gap-2 text-sm p-3 rounded-lg bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100 text-left"
        >
          <MessageSquare size={16} className="flex-shrink-0" />
          <span className="flex-1">Tienes notas nuevas de asesorados. Toca para abrirlas.</span>
          <span className="text-xs font-semibold underline flex-shrink-0">Ver notas</span>
        </button>
      ) : null}

      {tab === 'students' ? (
        <div>
          <div className="flex flex-wrap gap-2 mb-4">
            <button
              type="button"
              onClick={() => showToast('Exportar Excel simulado (demo)')}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-medium"
            >
              Exportar Excel
            </button>
            <button
              type="button"
              onClick={() => setAction({ type: 'adherence' })}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium"
            >
              <Activity size={14} /> Adherencia
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            {[
              { label: 'Total', value: stats.total, color: 'bg-gray-50 border-gray-200 text-gray-700' },
              { label: '🟢 Verde', value: stats.green, color: 'bg-green-50 border-green-200 text-green-700' },
              { label: '🟡 Amarillo', value: stats.yellow, color: 'bg-yellow-50 border-yellow-200 text-yellow-700' },
              { label: '🔴 Rojo', value: stats.red, color: 'bg-red-50 border-red-200 text-red-700' },
            ].map((s) => (
              <div key={s.label} className={`rounded-xl border p-3 text-center ${s.color}`}>
                <p className="text-2xl font-bold">{s.value}</p>
                <p className="text-xs font-medium opacity-70">{s.label}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-3 mb-4">
            <input
              className="input flex-1 min-w-[160px]"
              placeholder="Buscar por nombre o correo..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select className="input w-40" value={trafficFilter} onChange={(e) => setTrafficFilter(e.target.value)}>
              <option value="">Todos</option>
              <option value="GREEN">🟢 Verde</option>
              <option value="YELLOW">🟡 Amarillo</option>
              <option value="RED">🔴 Rojo</option>
            </select>
          </div>

          <div className="space-y-2">
            {filtered.map((s) => (
              <div key={s.id} className="card">
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0 ${tlAvatar(s.trafficLight)}`}>
                    {s.firstName?.[0]}{s.lastName?.[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-semibold text-gray-800">{s.firstName} {s.lastName}</p>
                      <span className="text-sm">{tlEmoji(s.trafficLight)}</span>
                    </div>
                    <p className="text-xs text-gray-500 truncate mb-2">
                      {s.email} · {s.adherence}% adherencia
                      {s.lastLog ? ` · último log ${s.lastLog}` : ' · sin registro reciente'}
                    </p>
                    <div className="flex gap-1.5 flex-wrap mt-1">
                      <button type="button" onClick={() => { setAction({ type: 'reminder', student: s }); setNoteText(`Hola ${s.firstName}, recuerda registrar tus medidas y reporte diario.`); }}
                        className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-gray-500 hover:text-blue-600 hover:bg-blue-50 border border-gray-200 rounded-lg">
                        <Bell size={13} /> Recordatorio
                      </button>
                      <button type="button" onClick={() => { setAction({ type: 'note', student: s }); setNoteText(''); }}
                        className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-gray-500 hover:text-amber-600 hover:bg-amber-50 border border-gray-200 rounded-lg">
                        <MessageSquare size={13} /> Enviar nota
                      </button>
                      <button type="button" onClick={() => showToast(`Link de video enviado a ${s.firstName}`)}
                        className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-gray-500 hover:text-green-600 hover:bg-green-50 border border-gray-200 rounded-lg">
                        <Video size={13} /> Video
                      </button>
                      <button type="button" onClick={() => setAction({ type: 'measures', student: s })}
                        className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 border border-gray-200 rounded-lg">
                        <Ruler size={13} /> Medidas
                      </button>
                      <button type="button" onClick={() => setAction({ type: 'reports', student: s })}
                        className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-gray-500 hover:text-teal-600 hover:bg-teal-50 border border-gray-200 rounded-lg">
                        <ClipboardList size={13} /> Reportes
                      </button>
                      <button type="button" onClick={() => setAction({ type: 'activity', student: s })}
                        className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-gray-500 hover:text-cyan-600 hover:bg-cyan-50 border border-gray-200 rounded-lg">
                        <BarChart2 size={13} /> Actividad
                      </button>
                      <button type="button" onClick={() => setAction({ type: 'notes', student: s })}
                        className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-gray-500 hover:text-orange-600 hover:bg-orange-50 border border-gray-200 rounded-lg">
                        <History size={13} /> Notas
                      </button>
                      <button type="button" onClick={() => setAction({ type: 'weight', student: s })}
                        className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-gray-500 hover:text-pink-600 hover:bg-pink-50 border border-gray-200 rounded-lg">
                        <TrendingUp size={13} /> Peso
                      </button>
                      <button type="button" onClick={() => setAction({ type: 'usual', student: s })}
                        className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 border border-gray-200 rounded-lg">
                        <BookOpen size={13} /> Habituales / Archivos
                      </button>
                      <button type="button" onClick={() => setAction({ type: 'unlink', student: s })}
                        className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-gray-500 hover:text-red-600 hover:bg-red-50 border border-gray-200 rounded-lg">
                        <Trash2 size={13} /> Desvincular
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {tab === 'code' ? (
        <div className="card max-w-md">
          <h2 className="font-bold text-gray-800 mb-2">Mi Código de Entrenador</h2>
          <p className="text-sm text-gray-500 mb-4">Comparte este código con tus clientes para que puedan vincularse a ti.</p>
          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl border-2 border-dashed border-primary-300">
            <span className="text-2xl font-mono font-bold text-primary-700 tracking-widest flex-1">{trainer.trainerCode}</span>
            <button
              type="button"
              onClick={() => {
                try { navigator.clipboard?.writeText(trainer.trainerCode); } catch { /* noop */ }
                setCopied(true);
                showToast('Código copiado');
                window.setTimeout(() => setCopied(false), 1500);
              }}
              className={`p-2 rounded-lg ${copied ? 'text-green-600 bg-green-50' : 'text-gray-400 hover:text-primary-600 hover:bg-primary-50'}`}
            >
              {copied ? <CheckCircle size={20} /> : <Copy size={20} />}
            </button>
          </div>
          <p className="text-sm text-gray-500 mt-4">
            <span className="font-medium text-gray-700">{students.length}</span> asesorado(s) vinculados
          </p>
        </div>
      ) : null}

      {action?.type === 'reports' ? (
        <ReportsFollowUpModal student={action.student} onClose={() => setAction(null)} />
      ) : null}

      {action?.type === 'usual' ? (
        <UsualFilesDemoModal student={action.student} onClose={() => setAction(null)} />
      ) : null}

      {action && action.type !== 'reports' && action.type !== 'usual' ? createPortal(
        <div className="food-plan-root food-portal-modal fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40" role="dialog" aria-modal="true">
          <div className="food-demo-modal-panel bg-white rounded-2xl border border-gray-200 shadow-xl w-full max-w-md p-5 space-y-3 text-gray-800">
            {action.type === 'reminder' ? (
              <>
                <h3 className="font-bold text-gray-800">Recordatorio · {action.student.firstName}</h3>
                <p className="text-xs text-gray-500">Mensaje que recibiría el asesorado (demo · sin envío real).</p>
                <textarea
                  className="input min-h-[100px] w-full rounded-lg border border-gray-200 bg-white text-gray-800 text-sm p-3"
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Mensaje del recordatorio…"
                />
                <div className="flex gap-2 justify-end">
                  <button type="button" className="btn-secondary text-sm" onClick={() => { setAction(null); setNoteText(''); }}>Cancelar</button>
                  <button
                    type="button"
                    className="btn-primary text-sm"
                    onClick={() => {
                      if (!noteText.trim()) return;
                      showToast(`Recordatorio enviado a ${action.student.firstName}`);
                      setAction(null);
                      setNoteText('');
                    }}
                  >
                    Enviar recordatorio
                  </button>
                </div>
              </>
            ) : null}

            {action.type === 'note' ? (
              <>
                <h3 className="font-bold text-gray-800">Enviar nota · {action.student.firstName}</h3>
                <textarea
                  className="input min-h-[100px] w-full rounded-lg border border-gray-200 bg-white text-gray-800 text-sm p-3"
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Escribe una nota para tu asesorado…"
                />
                <div className="flex gap-2 justify-end">
                  <button type="button" className="btn-secondary text-sm" onClick={() => { setAction(null); setNoteText(''); }}>Cancelar</button>
                  <button type="button" className="btn-primary text-sm" onClick={sendNote}>Enviar nota</button>
                </div>
              </>
            ) : null}

            {action.type === 'measures' ? (
              <>
                <h3 className="font-bold text-gray-800">Medidas · {action.student.firstName}</h3>
                <p className="text-sm text-gray-600">Peso actual: <strong>{action.student.weight} kg</strong></p>
                <p className="text-xs text-gray-500">En producción verías historial + fotos. Aquí es simulado.</p>
                <button type="button" className="btn-secondary text-sm w-full" onClick={() => setAction(null)}>Cerrar</button>
              </>
            ) : null}

            {action.type === 'activity' ? (
              <>
                <h3 className="font-bold text-gray-800">Actividad · {action.student.firstName}</h3>
                <p className="text-sm text-gray-600">Adherencia últimos 30 días: <strong>{action.student.adherence}%</strong></p>
                <p className="text-xs font-medium mt-2">
                  {action.student.adherence >= 70 ? '🟢 Excelente adherencia'
                    : action.student.adherence >= 40 ? '🟡 Adherencia regular'
                      : '🔴 Adherencia baja — requiere atención'}
                </p>
                <button type="button" className="btn-secondary text-sm w-full" onClick={() => setAction(null)}>Cerrar</button>
              </>
            ) : null}

            {action.type === 'notes' ? (
              <>
                <h3 className="font-bold text-gray-800">Historial de notas · {action.student.firstName}</h3>
                {(action.student.notes || []).length === 0 ? (
                  <p className="text-sm text-gray-400">No has enviado notas a este asesorado aún</p>
                ) : (
                  <ul className="space-y-2 max-h-48 overflow-y-auto">
                    {action.student.notes.map((n) => (
                      <li key={n.id} className="rounded-lg bg-amber-50 border border-amber-100 p-2 text-sm text-gray-700">
                        <p>{n.text}</p>
                        <p className="text-xs text-gray-400 mt-1">{n.at}</p>
                      </li>
                    ))}
                  </ul>
                )}
                <button type="button" className="btn-secondary text-sm w-full" onClick={() => setAction(null)}>Cerrar</button>
              </>
            ) : null}

            {action.type === 'weight' ? (
              <>
                <h3 className="font-bold text-gray-800">Progreso de peso · {action.student.firstName}</h3>
                <p className="text-2xl font-bold text-gray-800">{action.student.weight} <span className="text-sm font-normal text-gray-400">kg</span></p>
                <p className="text-xs text-gray-500">Últimos registros simulados del demo.</p>
                <button type="button" className="btn-secondary text-sm w-full" onClick={() => setAction(null)}>Cerrar</button>
              </>
            ) : null}

            {action.type === 'adherence' ? (
              <>
                <h3 className="font-bold text-gray-800">Adherencia de uso (30 días)</h3>
                <ul className="space-y-2">
                  {students.map((s) => (
                    <li key={s.id} className="flex justify-between text-sm border-b border-gray-50 py-1.5">
                      <span className="text-gray-700">{s.firstName} {s.lastName}</span>
                      <span className="font-semibold text-gray-800">{s.adherence}%</span>
                    </li>
                  ))}
                </ul>
                <button type="button" className="btn-secondary text-sm w-full" onClick={() => setAction(null)}>Cerrar</button>
              </>
            ) : null}

            {action.type === 'unlink' ? (
              <>
                <h3 className="font-bold text-gray-800">¿Desvincular a {action.student.firstName}?</h3>
                <p className="text-sm text-gray-500">Solo afecta este demo en tu navegador.</p>
                <div className="flex gap-2 justify-end">
                  <button type="button" className="btn-secondary text-sm" onClick={() => setAction(null)}>Cancelar</button>
                  <button type="button" className="btn-primary text-sm" onClick={() => unlink(action.student)}>Desvincular</button>
                </div>
              </>
            ) : null}
          </div>
        </div>,
        document.body,
      ) : null}
    </div>
  );
}

function FinalUserViews({ view, setView, food, patchFood, visitorName, guideFocus }) {
  const [showStats, setShowStats] = useState(true);
  const [calendar] = useState(() => buildCalendarMarked());
  const [calMonth] = useState(new Date().getMonth() + 1);
  const [calYear] = useState(new Date().getFullYear());

  const todayLabel = useMemo(() => {
    try {
      return new Date().toLocaleDateString('es-CO', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
      });
    } catch {
      return todayYmd();
    }
  }, []);

  useEffect(() => {
    if (!guideFocus) return;
    document.getElementById(`demo-${guideFocus}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [guideFocus, view]);

  const overallPct = Math.min(100, Math.round((food.today_kcal / Math.max(1, food.goal_kcal)) * 100));
  const nutritionStats = {
    streak: 3,
    overall: overallPct,
    calories: overallPct,
    protein: Math.min(100, Math.round((food.today_protein / Math.max(1, food.goal_protein)) * 100)),
    carbs: Math.min(100, Math.round((food.today_carbs / Math.max(1, food.goal_carbs)) * 100)),
    fat: Math.min(100, Math.round((food.today_fat / Math.max(1, food.goal_fat)) * 100)),
  };

  const addMeal = (item) => {
    patchFood((prev) => ({
      ...prev,
      meals: [...prev.meals, { ...item, id: uid('m') }],
      today_kcal: prev.today_kcal + item.kcal,
      today_protein: prev.today_protein + item.protein,
      today_carbs: prev.today_carbs + (item.carbs || 0),
      today_fat: prev.today_fat + (item.fat || 0),
    }));
  };

  const updateWater = (delta) => {
    patchFood((prev) => ({
      ...prev,
      water_glasses: Math.max(0, Math.min(12, prev.water_glasses + delta)),
    }));
  };

  const firstDayOfMonth = new Date(calYear, calMonth - 1, 1).getDay();
  const daysInMonth = new Date(calYear, calMonth, 0).getDate();
  const monthName = new Date(calYear, calMonth - 1, 1).toLocaleDateString('es-CO', { month: 'long', year: 'numeric' });
  const todayStr = todayYmd();

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      {view === 'dashboard' ? (
        <>
          <div className="mb-6">
            <h1 className="text-xl md:text-2xl font-bold text-gray-800">¡Hola, {visitorName}! 👋</h1>
            <p className="text-gray-500 text-sm mt-1 capitalize">{todayLabel}</p>
            <p className="text-gray-500 text-sm mt-1">Plan autónomo · demo sin BD</p>
          </div>

          <div className="card mb-4">
            <div className="flex items-center justify-between cursor-pointer" onClick={() => setShowStats(!showStats)}>
              <h2 className="font-bold text-gray-800 flex items-center gap-2">📊 Estado Nutricional</h2>
              <div className="flex items-center gap-2 bg-orange-50 border border-orange-200 px-3 py-1.5 rounded-xl">
                <Flame size={16} className="text-orange-500" />
                <span className="font-bold text-orange-700 text-sm">{nutritionStats.streak} días</span>
                <span className="text-xs text-orange-500">racha</span>
              </div>
            </div>
            <div className={`mt-3 flex items-center gap-3 px-3 py-2 rounded-xl ${
              food.today_kcal >= food.goal_kcal * 0.8 ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'
            }`}>
              <span className="text-lg">{food.today_kcal >= food.goal_kcal * 0.8 ? '🟢' : '🟡'}</span>
              <div className="flex-1">
                <p className={`text-xs font-bold ${food.today_kcal >= food.goal_kcal * 0.8 ? 'text-green-700' : 'text-yellow-700'}`}>
                  {food.today_kcal >= food.goal_kcal * 0.8
                    ? '✅ ¡Llevas un excelente registro hoy!'
                    : '⚠️ Registro incompleto — sigue registrando'}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">{food.today_kcal} / {food.goal_kcal} kcal registradas hoy</p>
              </div>
            </div>
            {showStats ? (
              <div className="mt-4 mb-2">
                <div className="flex justify-between items-center mb-1.5">
                  <span className="text-sm text-gray-600 font-medium">Cumplimiento general</span>
                  <span className="text-base font-extrabold text-yellow-600">{nutritionStats.overall}%</span>
                </div>
                <div className="w-full h-4 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full bg-yellow-400" style={{ width: `${nutritionStats.overall}%` }} />
                </div>
                <div className="grid grid-cols-2 gap-3 mt-4">
                  {[
                    { label: '🔥 Calorías', value: nutritionStats.calories, color: 'bg-orange-400' },
                    { label: '🥩 Proteína', value: nutritionStats.protein, color: 'bg-red-400' },
                    { label: '🌾 Carbos', value: nutritionStats.carbs, color: 'bg-yellow-400' },
                    { label: '🫒 Grasas', value: nutritionStats.fat, color: 'bg-blue-400' },
                  ].map((m) => (
                    <div key={m.label} className="bg-gray-50 rounded-xl p-3">
                      <div className="flex justify-between items-center mb-1.5">
                        <span className="text-xs text-gray-600">{m.label}</span>
                        <span className="text-xs font-bold text-gray-700">{m.value}%</span>
                      </div>
                      <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${m.color}`} style={{ width: `${m.value}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <MacroCard label="Calorías" current={food.today_kcal} goal={food.goal_kcal} color="bg-orange-500" icon={Zap} unit="kcal" />
            <MacroCard label="Proteínas" current={food.today_protein} goal={food.goal_protein} color="bg-red-500" icon={Beef} />
            <MacroCard label="Carbos" current={food.today_carbs} goal={food.goal_carbs} color="bg-yellow-500" icon={Apple} />
            <MacroCard label="Grasas" current={food.today_fat} goal={food.goal_fat} color="bg-blue-500" icon={Droplets} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="card">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-bold text-gray-800 flex items-center gap-2">
                  <Droplets size={18} className="text-blue-500" /> Agua de hoy
                </h2>
                <span className="text-sm text-gray-500">{food.water_glasses} / 8 vasos</span>
              </div>
              <div className="flex flex-wrap gap-2 mb-3">
                {Array.from({ length: 8 }).map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => updateWater(i < food.water_glasses ? -(food.water_glasses - i) : i - food.water_glasses + 1)}
                    className="transition-transform hover:scale-110 focus:outline-none"
                  >
                    <svg width="28" height="36" viewBox="0 0 28 36">
                      <path d="M6 8 L4 28 Q4 33 14 33 Q24 33 24 28 L22 8 Z"
                        fill={i < food.water_glasses ? '#3b82f6' : '#e5e7eb'}
                        stroke={i < food.water_glasses ? '#2563eb' : '#d1d5db'} strokeWidth="1" />
                    </svg>
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => updateWater(-1)} className="flex-1 py-1.5 text-sm border rounded-lg text-gray-600 hover:bg-gray-50">− Quitar</button>
                <button type="button" onClick={() => updateWater(1)} className="flex-1 py-1.5 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600">+ Agregar</button>
              </div>
            </div>
            <div className="card">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-bold text-gray-800 text-sm">🚶 Pasos de hoy</h2>
                <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full font-medium">Meta: 8.000</span>
              </div>
              <div className="mb-3">
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>{food.steps.toLocaleString()} pasos</span>
                  <span>{Math.min(100, Math.round((food.steps / 8000) * 100))}%</span>
                </div>
                <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-green-400 rounded-full" style={{ width: `${Math.min(100, (food.steps / 8000) * 100)}%` }} />
                </div>
              </div>
              <div className="flex gap-1.5">
                {[1000, 2000, 5000].map((n) => (
                  <button key={n} type="button" onClick={() => patchFood({ steps: food.steps + n })}
                    className="flex-1 py-1.5 text-xs border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 font-medium">
                    +{(n / 1000).toFixed(0)}k
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <h2 className="font-bold text-gray-800 mb-3 capitalize">{monthName}</h2>
            <div className="grid grid-cols-7 gap-1 mb-1">
              {DAYS_ES.map((d) => <div key={d} className="text-center text-xs text-gray-400 font-medium py-1">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {Array.from({ length: firstDayOfMonth }).map((_, i) => <div key={`e-${i}`} />)}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const day = i + 1;
                const dateStr = `${calYear}-${String(calMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                const done = !!calendar[dateStr];
                const isToday = dateStr === todayStr;
                return (
                  <button
                    key={dateStr}
                    type="button"
                    className={`food-cal-day text-xs py-1 rounded-full mx-auto w-7 h-7 flex items-center justify-center font-medium ${
                      done ? 'food-cal-day--done bg-primary-500 text-white'
                        : isToday ? 'food-cal-day--today border-2 border-primary-400 text-primary-600'
                          : 'food-cal-day--idle text-gray-500 hover:bg-gray-100'
                    }`}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="card">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-bold text-gray-800 text-sm">🍽️ Resumen del día</h2>
              <button type="button" className="text-primary-600 text-sm font-medium hover:underline" onClick={() => setView('nutrition')}>
                + Registrar alimentos
              </button>
            </div>
            <ul className="space-y-2">
              {food.meals.map((m) => (
                <li key={m.id} className="flex justify-between text-sm border border-gray-100 rounded-lg px-3 py-2">
                  <span className="text-gray-700">{m.meal}: {m.name}</span>
                  <span className="text-gray-500 font-medium">{m.kcal} kcal</span>
                </li>
              ))}
            </ul>
          </div>
        </>
      ) : null}

      {view === 'nutrition' ? (
        <DemoNutritionView food={food} addMeal={addMeal} />
      ) : null}

      {view === 'plan' ? (
        <DemoPlanView food={food} />
      ) : null}

      {view === 'measures' ? (
        <DemoMeasuresView />
      ) : null}

      {view === 'report' ? (
        <DemoReportView />
      ) : null}

      {view === 'recipes' ? (
        <DemoRecipesView addMeal={addMeal} setView={setView} />
      ) : null}

      {view === 'chat' ? (
        <DemoChatView visitorName={visitorName} />
      ) : null}

      {view === 'mytrainer' ? (
        <div className="space-y-4 max-w-xl">
          <h1 className="text-xl font-bold text-gray-800">Mi entrenador</h1>
          <div className="card space-y-2">
            <p className="text-lg font-bold text-gray-800">Alex Entrenador</p>
            <p className="text-sm text-gray-500">Código DEMO-FOOD-42</p>
            <p className="text-sm text-gray-600">Te armó el plan de bajar de peso, las metas de agua y de pasos, y lee tu reporte diario para ajustar lo que sigue.</p>
          </div>
        </div>
      ) : null}

      {view === 'profile' ? (
        <div className="space-y-4 max-w-xl">
          <h1 className="text-xl font-bold text-gray-800">Perfil</h1>
          <div className="card space-y-2 text-sm text-gray-700">
            <p><strong>Nombre:</strong> {visitorName}</p>
            <p><strong>Rol:</strong> Usuario Food Plan</p>
            <p><strong>Plan:</strong> {food.plan.meta} · {food.goal_kcal} kcal</p>
            <p className="text-xs text-gray-400">Estos datos viven solo en este demo.</p>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function TrainerVigencias({ rows, setState }) {
  const [tab, setTab] = useState('licenses');
  const sorted = [...rows].sort((a, b) => Number(a.days_left) - Number(b.days_left));
  const alerts = sorted.filter((r) => Number(r.days_left) <= 0);
  const expiring = sorted.filter((r) => (r.estado === 'activo' || r.estado === 'revisado') && Number(r.days_left) >= 1 && Number(r.days_left) <= 30);
  const list = tab === 'alerts' ? alerts : tab === 'expiring' ? expiring : sorted;

  const confirm = (id) => {
    setState((prev) => ({
      ...prev,
      foodTrainer: {
        ...prev.foodTrainer,
        vigencias: (prev.foodTrainer.vigencias || []).map((r) => (
          r.id === id ? { ...r, estado: 'activo', days_left: 30 } : r
        )),
      },
    }));
  };

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-4">
      <h1 className="text-xl font-bold text-[#111827]">Vigencias Food Plan</h1>
      <p className="text-sm text-gray-500">Como las ve el entrenador: sin Pendientes y sin marcar Sin ingreso.</p>
      <div className="flex flex-wrap gap-2">
        {[
          { id: 'alerts', label: `Alertas (${alerts.length})` },
          { id: 'expiring', label: `Por vencer (${expiring.length})` },
          { id: 'licenses', label: `Licencias (${sorted.length})` },
        ].map((t) => (
          <button key={t.id} type="button" className={tab === t.id ? 'btn-primary text-sm' : 'btn-secondary text-sm'} onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-500">
            <tr>
              <th className="px-3 py-2">Usuario</th>
              <th className="px-3 py-2">Plan</th>
              <th className="px-3 py-2">Estado</th>
              <th className="px-3 py-2">Días</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {list.map((r) => (
              <tr key={r.id} className="border-t border-gray-50">
                <td className="px-3 py-2 font-medium">{r.nombre}</td>
                <td className="px-3 py-2">{r.plan}</td>
                <td className="px-3 py-2">{r.estado}</td>
                <td className="px-3 py-2">{r.days_left}d</td>
                <td className="px-3 py-2">
                  {r.estado === 'inactivo' ? (
                    <button type="button" className="btn-primary text-xs py-1 px-2" onClick={() => confirm(r.id)}>Confirmar</button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DemoFood({ state, setState, onBackToEcosystem }) {
  const persona = state.ui.foodPersona === 'entrenador' ? 'entrenador' : 'final';
  const view = state.ui.foodView;
  const food = state.foodFinal;
  const nav = persona === 'entrenador' ? FOOD_NAV_ENTRENADOR : FOOD_NAV_FINAL;
  const trainer = state.foodTrainer;

  useEffect(() => {
    document.body.classList.add('food-plan-active');
    return () => document.body.classList.remove('food-plan-active');
  }, []);

  const setPersona = (foodPersona) => {
    setState((prev) => ({
      ...prev,
      ui: {
        ...prev.ui,
        foodPersona,
        foodView: foodPersona === 'entrenador' ? 'panel' : 'dashboard',
        foodTrainerTab: 'students',
        guideOpen: true,
        guideStep: 0,
      },
    }));
  };

  const guide = persona === 'entrenador' ? TRAINER_GUIDE : FINAL_GUIDE;
  const guideStep = Math.min(state.ui.guideStep || 0, guide.length - 1);
  const guideItem = guide[guideStep];

  const moveGuide = (delta) => {
    const next = Math.max(0, Math.min(guide.length - 1, guideStep + delta));
    if (delta > 0 && guideStep >= guide.length - 1) {
      setState((prev) => ({ ...prev, ui: { ...prev.ui, guideOpen: false } }));
      return;
    }
    const item = guide[next];
    setState((prev) => ({
      ...prev,
      ui: { ...prev.ui, guideStep: next, guideOpen: true, foodView: item.view },
    }));
  };

  const setView = (foodView) => {
    setState((prev) => ({ ...prev, ui: { ...prev.ui, foodView } }));
  };

  const patchFood = (updater) => {
    setState((prev) => ({
      ...prev,
      foodFinal: typeof updater === 'function' ? updater(prev.foodFinal) : { ...prev.foodFinal, ...updater },
    }));
  };

  const visitorName = String(state.ui.visitorName || '').trim() || 'Alex';
  const displayName = persona === 'entrenador'
    ? `${trainer.firstName} ${trainer.lastName}`
    : visitorName;
  const roleLabel = persona === 'entrenador' ? 'Entrenador' : 'Usuario';
  const icons = persona === 'entrenador' ? TRAINER_ICONS : USER_ICONS;

  return (
    <div className="food-plan-root flex h-screen bg-gray-50 overflow-hidden food-app-shell">
      <aside
        className="food-sidebar hidden md:flex flex-col flex-shrink-0 w-60"
        style={{ background: '#64ba30' }}
      >
        <div className="border-b border-white/20 px-3 py-3">
          <div className="rounded-xl overflow-hidden">
            <img
              src={foodLogoMark}
              alt="Food Plan"
              className="w-full h-auto max-h-[72px] object-contain object-left"
              style={{ display: 'block' }}
            />
          </div>
        </div>

        <div className="mx-3 mt-4 mb-2 p-3 rounded-xl bg-white/10 border border-white/20">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
              {displayName.split(/\s+/).map((p) => p[0]).slice(0, 2).join('')}
            </div>
            <div className="flex-1 min-w-0">
              <p className="food-sidebar-user text-white font-medium text-sm truncate">{displayName}</p>
              <span className="food-sidebar-muted text-xs text-white/70">{roleLabel}</span>
            </div>
          </div>
        </div>

        <nav className="flex-1 px-2 py-2 space-y-0.5 overflow-y-auto">
          {nav.map((item) => {
            const Icon = icons[item.id] || LayoutDashboard;
            const active = view === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => setView(item.id)}
                className={`food-sidebar-nav flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all text-sm w-full ${
                  active
                    ? 'food-sidebar-nav--active bg-white font-semibold shadow-sm'
                    : 'text-white/90 hover:bg-white/15 hover:text-white'
                }`}
              >
                <Icon size={18} className="flex-shrink-0" />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="p-2 border-t border-white/20 space-y-0.5">
          <button
            type="button"
            onClick={onBackToEcosystem}
            className="food-sidebar-nav flex items-center gap-3 px-3 py-2.5 rounded-xl text-white bg-white/20 hover:bg-white/30 w-full text-sm font-semibold"
          >
            <ArrowLeft size={18} />
            <span>Volver al inicio</span>
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <header className="md:hidden food-main-chrome flex items-center justify-between gap-2 px-3 py-2 bg-white border-b border-gray-100">
          <img src={foodLogoMark} alt="Food Plan" className="h-8 w-auto object-contain" />
          <select className="input text-sm max-w-[11rem]" value={view} onChange={(e) => setView(e.target.value)}>
            {nav.map((item) => (
              <option key={item.id} value={item.id}>{item.label}</option>
            ))}
          </select>
        </header>

        <main className="food-main-content flex-1 overflow-auto bg-gray-50">
          <div className="px-4 pt-4 md:px-6 md:pt-6 max-w-6xl mx-auto">
            <div className="flex flex-wrap items-center gap-2">
              <PersonaSwitch persona={persona} setPersona={setPersona} onBackToEcosystem={onBackToEcosystem} />
              {!state.ui.guideOpen ? (
                <button
                  type="button"
                  className="btn-secondary text-xs py-1.5 px-3"
                  onClick={() => {
                    const item = (persona === 'entrenador' ? TRAINER_GUIDE : FINAL_GUIDE)[0];
                    setState((prev) => ({
                      ...prev,
                      ui: { ...prev.ui, guideOpen: true, guideStep: 0, foodView: item.view },
                    }));
                  }}
                >
                  Ver la guía
                </button>
              ) : null}
            </div>
          </div>

          {persona === 'final' ? (
            <FinalUserViews
              view={view}
              setView={setView}
              food={food}
              patchFood={patchFood}
              visitorName={visitorName}
              guideFocus={state.ui.guideOpen ? guideItem?.focus : ''}
            />
          ) : null}

          {persona === 'entrenador' && (view === 'panel' || view === 'dashboard') ? (
            <TrainerPanel state={state} setState={setState} />
          ) : null}

          {persona === 'entrenador' && view === 'localmeals' ? (
            <div className="p-4 md:p-6 max-w-3xl mx-auto space-y-4">
              <h1 className="text-xl font-bold text-gray-800">Comidas habituales</h1>
              <p className="text-sm text-gray-500">Platos comerciales que el usuario elige en Nutrición.</p>
              <div className="card space-y-2">
                {(trainer.localMeals || DEMO_LOCAL_MEALS).map((meal) => (
                  <div key={meal.id} className="flex justify-between text-sm border border-gray-100 rounded-lg px-3 py-2">
                    <span>{meal.restaurant ? `${meal.restaurant} · ` : ''}{meal.name}</span>
                    <span className="text-xs text-[#64ba30]">Visible en el demo</span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {persona === 'entrenador' && view === 'vigencias' ? (
            <TrainerVigencias rows={trainer.vigencias || []} setState={setState} />
          ) : null}

          {persona === 'entrenador' && view === 'notifications' ? (
            <div className="p-4 md:p-6 max-w-3xl mx-auto space-y-4">
              <h1 className="text-xl font-bold text-gray-800">Notificaciones</h1>
              <div className="card space-y-3">
                {(trainer.inbox || []).map((n) => (
                  <div key={n.id} className={`rounded-lg p-3 border ${n.unread ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-100'}`}>
                    <p className="text-sm text-gray-700">{n.text}</p>
                    <p className="text-xs text-gray-400 mt-1">{n.from} · {n.at}</p>
                  </div>
                ))}
                <button
                  type="button"
                  className="btn-secondary text-sm"
                  onClick={() => setState((prev) => ({
                    ...prev,
                    foodTrainer: {
                      ...prev.foodTrainer,
                      inbox: (prev.foodTrainer.inbox || []).map((n) => ({ ...n, unread: false })),
                    },
                  }))}
                >
                  Marcar como leídas
                </button>
              </div>
            </div>
          ) : null}

          {persona === 'entrenador' && view === 'reminder' ? (
            <div className="p-4 md:p-6 max-w-3xl mx-auto space-y-4">
              <h1 className="text-xl font-bold text-gray-800">Recordatorio Medidas</h1>
              <div className="card space-y-3">
                <p className="text-sm text-gray-600">
                  Configura cada cuántos días recordar a tus asesorados que actualicen medidas (simulado).
                </p>
                <label className="text-sm text-gray-600 space-y-1 block max-w-xs">
                  Frecuencia (días)
                  <input className="input" type="number" defaultValue={14} />
                </label>
                <button type="button" className="btn-primary text-sm">Guardar (demo)</button>
              </div>
            </div>
          ) : null}

          {persona === 'entrenador' && view === 'profile' ? (
            <div className="p-4 md:p-6 max-w-3xl mx-auto space-y-4">
              <h1 className="text-xl font-bold text-gray-800">Perfil</h1>
              <div className="card space-y-2 text-sm text-gray-700">
                <p><strong>Nombre:</strong> {trainer.firstName} {trainer.lastName}</p>
                <p><strong>Rol:</strong> Entrenador Food Plan</p>
                <p><strong>Código:</strong> {trainer.trainerCode}</p>
                <p><strong>Asesorados:</strong> {(trainer.students || []).length}</p>
              </div>
            </div>
          ) : null}
        </main>
      </div>
      {state.ui.guideOpen && guideItem ? (
        <DemoGuide
          step={guideStep}
          total={guide.length}
          title={guideItem.title}
          body={guideItem.body}
          onBack={() => moveGuide(-1)}
          onNext={() => moveGuide(1)}
          onClose={() => setState((prev) => ({ ...prev, ui: { ...prev.ui, guideOpen: false } }))}
        />
      ) : null}
    </div>
  );
}

export default DemoFood;
